import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Package, MessageSquare, TrendingUp, Users, ArrowUpRight, RefreshCw, 
  Sparkles, Bot, Target, Mail, Clock, ChevronLeft, ChevronRight, Zap, Search,
  Shield, Lock, CheckCircle, Globe, Activity, User, FileText, Send, Loader2, X,
  Video, AlertCircle, Scan
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import VideoVerificationModal from '@/components/VideoVerificationModal';
import CompactProfileProgress from '@/components/CompactProfileProgress';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Activity Feed Component
const ActivityFeed = ({ userRole }) => {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchActivities = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/activities?limit=10`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setActivities(response.data.activities || []);
    } catch (error) {
      console.error('Failed to fetch activities:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchActivities();
    // Refresh activities every 30 seconds
    const interval = setInterval(fetchActivities, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatTime = (timestamp) => {
    if (!timestamp) return 'Just now';
    const now = new Date();
    const then = new Date(timestamp);
    const diffMins = Math.floor((now - then) / (1000 * 60));
    
    if (diffMins < 1) return 'Just now';
    if (diffMins === 1) return '1 min ago';
    if (diffMins < 60) return `${diffMins} mins ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return `${Math.floor(diffMins / 1440)}d ago`;
  };

  const getActivityIcon = (type) => {
    const icons = {
      inquiry_received: MessageSquare,
      inquiry_sent: Send,
      ai_draft_created: Bot,
      ai_draft_approve: CheckCircle,
      ai_draft_edit: FileText,
      ai_draft_reject: X,
      lead_stage_changed: TrendingUp,
      product_created: Package,
      default: Activity
    };
    return icons[type] || icons.default;
  };

  const getActivityStyle = (type) => {
    const styles = {
      inquiry_received: { color: 'text-blue-500', bg: 'bg-blue-50' },
      inquiry_sent: { color: 'text-green-500', bg: 'bg-green-50' },
      ai_draft_created: { color: 'text-purple-500', bg: 'bg-purple-50' },
      ai_draft_approve: { color: 'text-green-500', bg: 'bg-green-50' },
      ai_draft_edit: { color: 'text-amber-500', bg: 'bg-amber-50' },
      ai_draft_reject: { color: 'text-red-500', bg: 'bg-red-50' },
      lead_stage_changed: { color: 'text-indigo-500', bg: 'bg-indigo-50' },
      product_created: { color: 'text-teal-500', bg: 'bg-teal-50' },
      default: { color: 'text-gray-500', bg: 'bg-gray-50' }
    };
    return styles[type] || styles.default;
  };

  return (
    <Card data-testid="activity-feed-card" className="h-full">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading flex items-center gap-2 text-base sm:text-lg">
            <Activity className="h-4 w-4 sm:h-5 sm:w-5 text-green-500" />
            <span className="hidden xs:inline">Your</span> Activity
          </CardTitle>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
            <span className="text-xs text-muted-foreground">Live</span>
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-3 sm:px-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : activities.length === 0 ? (
          <div className="text-center py-6 sm:py-8">
            <Activity className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-muted-foreground/30 mb-3" />
            <p className="text-sm text-muted-foreground">No recent activity</p>
            <p className="text-xs text-muted-foreground mt-1 px-2">
              {userRole === 'seller' 
                ? 'Add products and receive inquiries to see activity here'
                : 'Browse products and send inquiries to see activity here'
              }
            </p>
          </div>
        ) : (
          <div className="space-y-2 sm:space-y-3 max-h-[300px] sm:max-h-[350px] overflow-y-auto">
            {activities.map((activity, index) => {
              const Icon = getActivityIcon(activity.type);
              const style = getActivityStyle(activity.type);
              return (
                <div 
                  key={activity.id || index}
                  className={`flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-lg ${style.bg} transition-all duration-300`}
                >
                  <div className={`p-1.5 sm:p-2 rounded-full bg-white ${style.color} flex-shrink-0`}>
                    <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
                  </div>
                  <div className="flex-1 min-w-0 overflow-hidden">
                    <p className="text-xs sm:text-sm font-medium text-gray-900 line-clamp-2 sm:line-clamp-1">
                      {activity.message}
                    </p>
                    {activity.detail && (
                      <p className="text-[10px] sm:text-xs text-gray-600 mt-0.5 line-clamp-1">{activity.detail}</p>
                    )}
                  </div>
                  <span className="text-[10px] sm:text-xs text-gray-500 whitespace-nowrap flex-shrink-0">
                    {formatTime(activity.created_at)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
        <div className="mt-3 sm:mt-4 pt-2 sm:pt-3 border-t">
          <p className="text-[10px] sm:text-xs text-center text-muted-foreground">
            Your personal activity updates in real-time
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

// Security Badges Component - Without false claims
const SecurityBadges = () => {
  return (
    <div className="bg-slate-900 rounded-xl p-4 mb-8" data-testid="security-badges">
      <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-8">
        <div className="flex items-center gap-2 text-white">
          <Lock className="h-5 w-5 text-green-400" />
          <div>
            <p className="text-xs font-semibold">256-bit SSL</p>
            <p className="text-[10px] text-gray-400">Encrypted</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-white">
          <Shield className="h-5 w-5 text-blue-400" />
          <div>
            <p className="text-xs font-semibold">India Servers</p>
            <p className="text-[10px] text-gray-400">Data hosted locally</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-white">
          <CheckCircle className="h-5 w-5 text-amber-400" />
          <div>
            <p className="text-xs font-semibold">Privacy First</p>
            <p className="text-[10px] text-gray-400">Never shared</p>
          </div>
        </div>
        <div className="flex items-center gap-2 text-white">
          <Sparkles className="h-5 w-5 text-purple-400" />
          <div>
            <p className="text-xs font-semibold">Free Beta</p>
            <p className="text-[10px] text-gray-400">No charges</p>
          </div>
        </div>
      </div>
      <p className="text-center text-[10px] text-gray-500 mt-3">
        Your buyer data is protected and never shared with third parties
      </p>
    </div>
  );
};

// AI Features Slideshow Component
const AIFeaturesSlideshow = ({ userRole }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const navigate = useNavigate();

  const buyerSlides = [
    {
      title: "Product Watchlist",
      description: "Track products you're interested in and get alerts when prices change. Never miss a good deal.",
      icon: Target,
      color: "from-purple-500 to-pink-600",
      action: () => navigate('/buyer-dashboard'),
      actionText: "My Watchlist"
    },
    {
      title: "AI Smart Match",
      description: "Find suppliers using natural language. Just type '4 tons of basmati rice' and our AI matches you with the best sellers.",
      icon: Search,
      color: "from-amber-500 to-orange-600",
      action: () => navigate('/marketplace'),
      actionText: "Try Smart Match"
    },
    {
      title: "Intelligent Recommendations",
      description: "Get personalized supplier recommendations with match scores and 3 specific reasons why each seller fits your needs.",
      icon: Target,
      color: "from-green-500 to-emerald-600",
      action: () => navigate('/marketplace'),
      actionText: "Browse Products"
    },
    {
      title: "AI-Powered Responses",
      description: "Receive instant, professional responses to your inquiries powered by AI - no more waiting for seller replies.",
      icon: Bot,
      color: "from-blue-500 to-indigo-600",
      action: () => navigate('/inquiries'),
      actionText: "View Inquiries"
    }
  ];

  const sellerSlides = [
    {
      title: "AI Auto-Response Drafts",
      description: "AI drafts professional responses to every inquiry automatically. Review, approve, and send - never miss a lead.",
      icon: Bot,
      color: "from-indigo-500 to-purple-600",
      action: () => navigate('/ai-drafts'),
      actionText: "View AI Drafts"
    },
    {
      title: "Smart Lead Scoring",
      description: "AI analyzes conversations and automatically advances leads through your pipeline based on buying signals.",
      icon: Target,
      color: "from-green-500 to-emerald-600",
      action: () => navigate('/leads'),
      actionText: "Manage Pipeline"
    },
    {
      title: "Automated Follow-ups",
      description: "30-day AI follow-up sequences keep your leads warm. Personalized drafts sent for your approval.",
      icon: Mail,
      color: "from-amber-500 to-orange-600",
      action: () => navigate('/followups'),
      actionText: "View Follow-ups"
    },
    {
      title: "Instant Response Time",
      description: "Respond to inquiries in under 5 minutes with AI-generated responses. Convert 30-70% more leads.",
      icon: Clock,
      color: "from-blue-500 to-cyan-600",
      action: () => navigate('/inquiries'),
      actionText: "View Inquiries"
    }
  ];

  const slides = userRole === 'buyer' ? buyerSlides : sellerSlides;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => setCurrentSlide((prev) => (prev + 1) % slides.length);
  const prevSlide = () => setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);

  return (
    <div className="relative mb-8 rounded-2xl overflow-hidden" data-testid="ai-features-slideshow">
      <div className="relative h-64 sm:h-56">
        {slides.map((slide, index) => {
          const Icon = slide.icon;
          return (
            <div
              key={index}
              className={`absolute inset-0 transition-opacity duration-700 ${
                index === currentSlide ? 'opacity-100' : 'opacity-0 pointer-events-none'
              }`}
            >
              <div className={`w-full h-full bg-gradient-to-br ${slide.color} p-6 sm:p-8 flex flex-col justify-center`}>
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-white/20 backdrop-blur-sm rounded-xl">
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Sparkles className="h-5 w-5 text-yellow-300" />
                      <span className="text-white/80 text-sm font-medium">AI-Powered</span>
                    </div>
                    <h3 className="text-2xl sm:text-3xl font-bold text-white mb-2">{slide.title}</h3>
                    <p className="text-white/90 text-sm sm:text-base mb-4 max-w-xl">{slide.description}</p>
                    <Button 
                      onClick={slide.action}
                      className="bg-white text-gray-900 hover:bg-white/90 font-semibold"
                      data-testid={`slideshow-action-${index}`}
                    >
                      <Zap className="h-4 w-4 mr-2" />
                      {slide.actionText}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Navigation Arrows */}
      <button
        onClick={prevSlide}
        className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all flex items-center justify-center text-white"
        data-testid="slideshow-prev"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-all flex items-center justify-center text-white"
        data-testid="slideshow-next"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dots Navigation */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentSlide ? 'bg-white w-6' : 'bg-white/50 hover:bg-white/75'
            }`}
            data-testid={`slideshow-dot-${index}`}
          />
        ))}
      </div>
    </div>
  );
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [user, setUser] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [showVideoVerification, setShowVideoVerification] = useState(false);

  const fetchStats = useCallback(async (showRefreshIndicator = false) => {
    if (showRefreshIndicator) setIsRefreshing(true);
    try {
      const response = await axios.get(`${API}/dashboard/stats`);
      setStats(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      if (showRefreshIndicator) setIsRefreshing(false);
    }
  }, []);

  useEffect(() => {
    const userData = JSON.parse(localStorage.getItem('user'));
    setUser(userData);
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const intervalId = setInterval(() => fetchStats(), 30000);
    
    return () => clearInterval(intervalId);
  }, [fetchStats]);

  // Refresh when window becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchStats();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [fetchStats]);

  const handleManualRefresh = () => {
    fetchStats(true);
  };

  const buyerStats = [
    { icon: MessageSquare, label: 'Total Inquiries', value: stats?.total_inquiries || 0, color: 'text-blue-600' },
    { icon: TrendingUp, label: 'Active Leads', value: stats?.active_leads || 0, color: 'text-green-600' },
    { icon: Package, label: 'Active Deals', value: stats?.active_deals || 0, color: 'text-amber-600' },
    { icon: Users, label: 'Products Available', value: stats?.products_available || 0, color: 'text-purple-600' }
  ];

  const sellerStats = [
    { icon: Package, label: 'Total Products', value: stats?.total_products || 0, color: 'text-blue-600' },
    { icon: MessageSquare, label: 'Total Inquiries', value: stats?.total_inquiries || 0, color: 'text-green-600' },
    { icon: TrendingUp, label: 'Active Leads', value: stats?.active_leads || 0, color: 'text-amber-600' },
    { icon: Users, label: 'Active Deals', value: stats?.active_deals || 0, color: 'text-purple-600' }
  ];

  const displayStats = user?.role === 'buyer' ? buyerStats : sellerStats;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="dashboard-page">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl font-bold text-primary mb-2" data-testid="dashboard-heading">
              Welcome back, {user?.full_name}
            </h1>
            <p className="text-muted-foreground">
              {user?.role === 'buyer' ? 'Discover products and manage your inquiries' : 'Manage your products and respond to inquiries'}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {lastUpdated && (
              <span className="text-xs text-muted-foreground">
                Updated {lastUpdated.toLocaleTimeString()}
              </span>
            )}
            <Button 
              variant="outline" 
              size="sm"
              onClick={handleManualRefresh}
              disabled={isRefreshing}
              data-testid="refresh-stats-btn"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        {/* Security Badges */}
        <SecurityBadges />

        {/* AI Features Slideshow */}
        <AIFeaturesSlideshow userRole={user?.role} />

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {displayStats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <Card key={index} className="stat-card" data-testid={`stat-card-${index}`}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">{stat.label}</p>
                      <p className="text-3xl font-bold font-heading">{stat.value}</p>
                    </div>
                    <div className={`p-3 rounded-lg bg-slate-50 ${stat.color}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Video Verification Section for Sellers and Buyers */}
        {(user?.role === 'seller' || user?.role === 'buyer') && (
          <div className="mb-8">
            {/* Video Verification Modal - Opens as full screen overlay */}
            <VideoVerificationModal
              isOpen={showVideoVerification}
              onClose={() => setShowVideoVerification(false)}
              fullName={user?.full_name}
              companyName={user?.company_name}
              userRole={user?.role}
              onVideoUploaded={() => {
                fetchStats();
                setShowVideoVerification(false);
              }}
            />
            
            {/* Verification Status Card */}
            <Card className={`border-2 ${
              user?.is_verified ? 'border-green-200 bg-green-50/50' :
              user?.video_verification_status === 'pending_review' ? 'border-yellow-200 bg-yellow-50/50' :
              'border-blue-200 bg-blue-50/50'
            }`} data-testid="verification-status-card">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-full ${
                      user?.is_verified ? 'bg-green-100' :
                      user?.video_verification_status === 'pending_review' ? 'bg-yellow-100' :
                      'bg-blue-100'
                    }`}>
                      {user?.is_verified ? (
                        <CheckCircle className="h-6 w-6 text-green-600" />
                      ) : user?.video_verification_status === 'pending_review' ? (
                        <Clock className="h-6 w-6 text-yellow-600" />
                      ) : (
                        <Scan className="h-6 w-6 text-blue-600" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">
                        {user?.is_verified ? `Verified ${user?.role === 'buyer' ? 'Buyer' : 'Seller'}` :
                         user?.video_verification_status === 'pending_review' ? 'Verification Under Review' :
                         'Complete Your Verification'}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {user?.is_verified ? 
                          (user?.role === 'buyer' ? 'Your identity has been verified. Sellers trust verified buyers more.' : 'Your identity has been verified. Buyers trust verified sellers more.') :
                         user?.video_verification_status === 'pending_review' ? 'Your video is being reviewed. Usually takes 1-2 business days.' :
                         `Follow 6 simple steps to verify your identity and build ${user?.role === 'buyer' ? 'seller' : 'buyer'} trust.`}
                      </p>
                    </div>
                  </div>
                  {!user?.is_verified && user?.video_verification_status !== 'pending_review' && (
                    <Button 
                      onClick={() => setShowVideoVerification(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="start-video-verification-btn"
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Start Verification
                    </Button>
                  )}
                  {user?.is_verified && (
                    <Badge className="bg-green-600 text-white">
                      <CheckCircle className="h-3 w-3 mr-1" />
                      Verified
                      </Badge>
                    )}
                    {user?.video_verification_status === 'pending_review' && (
                      <Badge className="bg-yellow-600 text-white">
                        <Clock className="h-3 w-3 mr-1" />
                        Under Review
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
          </div>
        )}

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Compact Profile Progress - First card */}
          {user?.id && (
            <div className="md:col-span-1">
              <CompactProfileProgress userId={user.id} userRole={user.role} />
            </div>
          )}
          {/* Quick Actions */}
          <Card data-testid="quick-actions-card">
            <CardHeader>
              <CardTitle className="font-heading">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {user?.role === 'buyer' ? (
                <>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between" 
                    onClick={() => navigate('/products')}
                    data-testid="browse-products-btn"
                  >
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4 text-amber-500" />
                      AI Smart Match Products
                    </span>
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between" 
                    onClick={() => navigate('/inquiries')}
                    data-testid="view-inquiries-btn"
                  >
                    View My Inquiries
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between" 
                    onClick={() => navigate('/marketplace')}
                    data-testid="browse-marketplace-btn"
                  >
                    Browse Marketplace
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                </>
              ) : (
                <>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between" 
                    onClick={() => navigate('/ai-drafts')}
                    data-testid="view-ai-drafts-btn"
                  >
                    <span className="flex items-center gap-2">
                      <Bot className="h-4 w-4 text-indigo-500" />
                      Review AI Response Drafts
                    </span>
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between" 
                    onClick={() => navigate('/products')}
                    data-testid="add-product-btn"
                  >
                    Add New Product
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between" 
                    onClick={() => navigate('/inquiries')}
                    data-testid="view-inquiries-btn"
                  >
                    View Inquiries
                    <ArrowUpRight className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button 
                variant="outline" 
                className="w-full justify-between" 
                onClick={() => navigate('/leads')}
                data-testid="manage-pipeline-btn"
              >
                Manage Pipeline
                <ArrowUpRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          {/* Live Activity Feed */}
          <ActivityFeed userRole={user?.role} />

          {/* AI Status */}
          <Card data-testid="ai-status-card">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-amber-500" />
                AI Status
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-green-900">Auto-Response</span>
                  </div>
                  <span className="text-xs text-green-700 font-medium">Active</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-green-900">Lead Scoring</span>
                  </div>
                  <span className="text-xs text-green-700 font-medium">Active</span>
                </div>
                <div className="flex items-center justify-between p-3 rounded-lg bg-green-50 border border-green-200">
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-medium text-green-900">Smart Matching</span>
                  </div>
                  <span className="text-xs text-green-700 font-medium">Active</span>
                </div>
                {user?.role === 'seller' && (
                  <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <span className="text-sm font-medium text-amber-900">Follow-ups</span>
                    </div>
                    <span className="text-xs text-amber-700 font-medium">Review</span>
                  </div>
                )}
              </div>
              
              {/* Trust Note */}
              <div className="mt-4 pt-3 border-t">
                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Shield className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                  <p>Your data is encrypted and processed on secure Indian servers. We never share buyer information.</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}
